/**
 * ADR-103: WooCommerceSurfaceAdapter — widget-side WC detection and action layer.
 *
 * Implements SurfaceAdapter for WooCommerce. Extracted from embed.ts per
 * PR-PLATFORM-REFACTOR-EXTRACT-WC-ADAPTER-V1. embed.ts delegates all
 * WC-specific DOM logic to this class.
 *
 * Mirrors core-types shapes locally — no cross-package runtime import in widget bundle.
 * See protocol.ts: "no cross-package import in widget bundle".
 */

import type {
  SurfaceAdapter,
  SurfaceAdapterCapabilities,
  CheckoutAction,
  NavigationAction,
  UnsubscribeFn,
} from '@orangepill/core-types';

// ── Local type mirrors (match @orangepill/core-types shapes structurally) ────

type SurfacePageType = 'product' | 'cart' | 'checkout' | 'shop' | 'account' | 'home' | 'other';

interface CheckoutTotals {
  total: number; currency: string;
  subtotal?: number; shipping?: number; tax?: number; discount?: number;
}
interface CheckoutContext {
  step: 'billing' | 'shipping' | 'payment' | 'processing' | 'review' | 'success' | 'error' | 'other';
  totals?: CheckoutTotals;
  orderReceived?: { orderId: string };
  errorState?: { message: string; type?: 'validation' | 'payment' | 'inventory' | 'other' };
}
interface VisibleLineItem {
  productName: string; quantity: number; lineId?: string; productLink?: string;
}
interface CartContext {
  isEmpty: boolean;
  itemCount?: number;
  total?: { amount: number; currency: string };
  visibleLineItems?: VisibleLineItem[];
  visibleLineItemsTruncated?: boolean;
  hasDiscount?: boolean;
}
interface SurfaceContext {
  pageType: SurfacePageType;
  url: string;
  canonicalUrl?: string;
  productId?: string;
  productName?: string;
  cartSummary?: { itemCount: number; currency?: string };
  checkoutContext?: CheckoutContext;
  cartContext?: CartContext;
  detectedAt: string;
}

// ── Constants ─────────────────────────────────────────────────────────────────

const CART_LINE_ITEM_CAP = 10;

// ── WooCommerceSurfaceAdapter ─────────────────────────────────────────────────

export class WooCommerceSurfaceAdapter implements SurfaceAdapter {
  readonly capabilities: SurfaceAdapterCapabilities = {
    navigation: true,
    checkoutActions: ['retryPayment'] as CheckoutAction[],
  };

  private onUpdate: (() => void) | null = null;
  private checkoutObserver: MutationObserver | null = null;
  private blocksCartObserver: MutationObserver | null = null;

  // ── SurfaceAdapter interface ──────────────────────────────────────────────

  detectSurfaceContext(): SurfaceContext {
    const bodyClasses = Array.from(document.body.classList);
    const { pathname } = window.location;
    const pageType = this.detectPageType(bodyClasses, pathname);
    const canonical = document.querySelector<HTMLLinkElement>('link[rel="canonical"]')?.href;

    let productId: string | undefined;
    let productName: string | undefined;
    if (pageType === 'product') {
      const jsonLd = this.extractJsonLdProduct();
      productId = jsonLd.productId
        // DOM fallbacks when JSON-LD is absent or @id lacks a parseable slug/id
        ?? (document.querySelector<HTMLElement>('[data-product_id]') as any)?.dataset?.product_id
        ?? document.querySelector<HTMLInputElement>('[name="add-to-cart"]')?.value;
      productName = jsonLd.productName
        ?? document.querySelector<HTMLElement>('h1.product_title, h1.entry-title')?.textContent?.trim().slice(0, 120)
        ?? (document.title.split(/[–|\-]/)[0].trim().slice(0, 120) || undefined);
    }

    let cartSummary: SurfaceContext['cartSummary'];
    if (pageType === 'cart' || pageType === 'checkout') {
      const itemCount = this.readCartCount();
      if (itemCount !== null) cartSummary = { itemCount };
    }

    let checkoutContext: CheckoutContext | undefined;
    if (pageType === 'checkout') {
      checkoutContext = this.detectCheckoutContext();
      this.setupCheckoutObserver();
    } else {
      this.teardownCheckoutObserver();
    }

    let cartContext: CartContext | undefined;
    if (pageType === 'cart') cartContext = this.detectCartContext();

    return {
      pageType,
      url: window.location.href,
      ...(canonical       ? { canonicalUrl: canonical }  : {}),
      ...(productId       ? { productId }                 : {}),
      ...(productName     ? { productName }               : {}),
      ...(cartSummary     ? { cartSummary }               : {}),
      ...(checkoutContext ? { checkoutContext }            : {}),
      ...(cartContext     ? { cartContext }                : {}),
      detectedAt: new Date().toISOString(),
    };
  }

  executeNavigationAction(_action: NavigationAction, url: string): void {
    window.location.href = url;
  }

  executeCheckoutAction(action: CheckoutAction): void {
    if (action !== 'retryPayment') return;
    const classicBtn = document.querySelector<HTMLButtonElement>('#place_order');
    const blocksBtn = document.querySelector<HTMLButtonElement>('.wc-block-components-checkout-place-order-button');
    const btn = classicBtn ?? blocksBtn;
    if (btn && !btn.disabled) btn.click();
  }

  listenForUpdates(onUpdate: () => void): UnsubscribeFn {
    this.onUpdate = onUpdate;
    const handler = () => onUpdate();
    // Classic WC: jQuery event fired after fragment refresh
    document.body.addEventListener('wc_fragments_refreshed', handler);
    // WC Blocks: React store updates don't fire wc_fragments_refreshed; observe DOM mutations instead
    this.setupBlocksCartObserver(onUpdate);
    return () => {
      document.body.removeEventListener('wc_fragments_refreshed', handler);
      this.teardownBlocksCartObserver();
      this.onUpdate = null;
      this.teardownCheckoutObserver();
    };
  }

  /** Trigger WC cart fragment refresh (called by embed.ts on WEBCHAT_CART_EVENT). */
  triggerCartRefresh(): void {
    if (typeof (window as any).jQuery !== 'undefined') {
      (window as any).jQuery(document.body).trigger('wc_fragment_refresh');
    } else {
      document.body.dispatchEvent(new CustomEvent('wc_fragment_refresh'));
    }
  }

  // ── Private detection helpers ─────────────────────────────────────────────

  private detectPageType(bodyClasses: string[], pathname: string): SurfacePageType {
    if (bodyClasses.includes('woocommerce-product-page')) return 'product';
    if (bodyClasses.includes('woocommerce-cart')) return 'cart';
    if (bodyClasses.includes('woocommerce-checkout')) return 'checkout';
    if (bodyClasses.includes('woocommerce-shop')) return 'shop';
    if (bodyClasses.includes('woocommerce-account')) return 'account';
    if (/\/product\//.test(pathname)) return 'product';
    if (/\/cart\b/.test(pathname)) return 'cart';
    if (/\/checkout\b/.test(pathname)) return 'checkout';
    if (/\/shop\b/.test(pathname)) return 'shop';
    if (/\/my-account\b/.test(pathname)) return 'account';
    if (pathname === '/' || pathname === '') return 'home';
    return 'other';
  }

  private extractJsonLdProduct(): { productId?: string; productName?: string } {
    try {
      const scripts = document.querySelectorAll('script[type="application/ld+json"]');
      for (let i = 0; i < scripts.length; i++) {
        const raw = scripts[i].textContent;
        if (!raw) continue;
        const data = JSON.parse(raw);
        const entries = Array.isArray(data) ? data : [data];
        for (const entry of entries) {
          if (entry['@type'] === 'Product') {
            const rawId: string | undefined = entry['@id'] ?? entry.url;
            let productId: string | undefined;
            if (rawId) {
              const slugMatch = rawId.match(/\/product\/([^/]+)\/?/);
              const numMatch = rawId.match(/[?&]p=(\d+)/);
              productId = slugMatch?.[1] ?? numMatch?.[1];
            }
            const productName: string | undefined = entry.name
              ? String(entry.name).slice(0, 120) : undefined;
            return { productId, productName };
          }
        }
      }
    } catch { /* JSON-LD parse failure */ }
    return {};
  }

  private readCartCount(): number | null {
    const selectors = ['.cart-count', '.cart-contents-count', '.cart_contents .count', '[data-cart-count]'];
    for (const sel of selectors) {
      const el = document.querySelector(sel);
      if (el) {
        const text = el.getAttribute('data-cart-count') ?? el.textContent ?? '';
        const n = parseInt(text.replace(/\D/g, ''), 10);
        if (!isNaN(n)) return n;
      }
    }
    return null;
  }

  private parseWCAmount(raw: string): number | null {
    try {
      // Classic WC pages inject woocommerce_params; WC Blocks pages use wcSettings.currency instead.
      const wcParams   = (window as any).woocommerce_params;
      const wcSettings = (window as any).wcSettings?.currency;
      const thousands  = (wcParams?.thousand_separator as string | undefined)
                      ?? (wcSettings?.thousandSeparator as string | undefined)
                      ?? ',';
      const decimal    = (wcParams?.decimal_separator  as string | undefined)
                      ?? (wcSettings?.decimalSeparator  as string | undefined)
                      ?? '.';
      // Escape separators for safe use inside a RegExp character class
      const esc = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      // Strip everything except digits and the two known separators (removes currency symbols, spaces, etc.)
      let clean = raw.replace(new RegExp(`[^0-9${esc(thousands)}${esc(decimal)}]`, 'g'), '');
      if (thousands) clean = clean.split(thousands).join('');
      if (decimal !== '.') clean = clean.replace(decimal, '.');
      const n = parseFloat(clean);
      return isNaN(n) ? null : n;
    } catch { return null; }
  }

  private readAmountFrom(selectors: string[]): number | null {
    for (const sel of selectors) {
      const el = document.querySelector<HTMLElement>(sel);
      if (!el) continue;
      const text = el.textContent ?? '';
      const n = this.parseWCAmount(text);
      if (n !== null) return n;
    }
    return null;
  }

  private extractCheckoutTotals(): CheckoutTotals | undefined {
    const total = this.readAmountFrom([
      '.woocommerce-checkout-review-order-table tr.order-total .woocommerce-Price-amount bdi',
      '.woocommerce-checkout-review-order-table tr.order-total .amount',
      '.wc-block-components-totals-footer-item .wc-block-formatted-money-amount',
      '.wc-block-components-totals-footer-item .wc-block-components-formatted-money-amount',
    ]);
    if (total === null) return undefined;
    const currency: string =
      (window as any).woocommerce_params?.currency_symbol
      ?? document.querySelector('.woocommerce-Price-currencySymbol')?.textContent
      ?? '';
    const subtotal = this.readAmountFrom([
      '.woocommerce-checkout-review-order-table tr.cart-subtotal .woocommerce-Price-amount bdi',
      '.woocommerce-checkout-review-order-table tr.cart-subtotal .amount',
      '.wc-block-components-totals-item--subtotal .wc-block-formatted-money-amount',
    ]);
    const shipping = this.readAmountFrom([
      '.woocommerce-checkout-review-order-table tr.shipping .woocommerce-Price-amount bdi',
      '.woocommerce-checkout-review-order-table tr.shipping .amount',
      '.wc-block-components-totals-shipping .wc-block-formatted-money-amount',
    ]);
    const tax = this.readAmountFrom([
      '.woocommerce-checkout-review-order-table tr.tax-total .woocommerce-Price-amount bdi',
      '.woocommerce-checkout-review-order-table tr.tax-total .amount',
      '.wc-block-components-totals-taxes .wc-block-formatted-money-amount',
    ]);
    const discount = this.readAmountFrom([
      '.woocommerce-checkout-review-order-table tr.cart-discount .woocommerce-Price-amount bdi',
      '.woocommerce-checkout-review-order-table tr.cart-discount .amount',
    ]);
    return {
      total, currency,
      ...(subtotal !== null ? { subtotal } : {}),
      ...(shipping !== null ? { shipping } : {}),
      ...(tax      !== null ? { tax }      : {}),
      ...(discount !== null ? { discount } : {}),
    };
  }

  private extractOrderIdFromUrl(): string | undefined {
    const match = window.location.pathname.match(/\/order-received\/(\d+)/);
    return match?.[1];
  }

  private detectErrorType(message: string): 'validation' | 'payment' | 'inventory' | 'other' {
    const lower = message.toLowerCase();
    if (/payment|card|transaction|declined|charge/.test(lower)) return 'payment';
    if (/stock|inventory|available|sold out/.test(lower)) return 'inventory';
    if (/required|invalid|enter|provide|complete/.test(lower)) return 'validation';
    return 'other';
  }

  private extractCheckoutErrorMessage(): CheckoutContext['errorState'] {
    const classicErrors = document.querySelectorAll<HTMLElement>(
      'ul.woocommerce-error li, .woocommerce-NoticeGroup .woocommerce-error li'
    );
    if (classicErrors.length > 0) {
      const messages = Array.from(classicErrors).map(el => el.textContent?.trim() ?? '').filter(Boolean);
      if (messages.length > 0) {
        const combined = messages.join(' ');
        return { message: combined, type: this.detectErrorType(combined) };
      }
    }
    const blockError = document.querySelector<HTMLElement>(
      '.wc-block-components-notice-banner--error .wc-block-components-notice-banner__content'
    ) ?? document.querySelector<HTMLElement>('.wc-block-components-notice-banner--error');
    if (blockError) {
      const message = blockError.textContent?.trim() ?? '';
      if (message) return { message, type: this.detectErrorType(message) };
    }
    return undefined;
  }

  private isCheckoutProcessing(): boolean {
    if (document.querySelector('#order_review .blockOverlay, #payment .blockOverlay')) return true;
    const placeOrder = document.querySelector<HTMLButtonElement>('#place_order');
    if (placeOrder?.disabled || placeOrder?.classList.contains('loading')) return true;
    if (document.querySelector('.wp-block-woocommerce-checkout[aria-busy="true"]')) return true;
    if (document.querySelector('.wc-block-components-checkout-place-order-button.busy')) return true;
    return false;
  }

  private detectCheckoutContext(): CheckoutContext | undefined {
    const bodyClasses = Array.from(document.body.classList);
    if (bodyClasses.includes('woocommerce-order-received')) {
      const orderId = this.extractOrderIdFromUrl();
      const totals = this.extractCheckoutTotals();
      return {
        step: 'success',
        ...(totals   ? { totals }                     : {}),
        ...(orderId  ? { orderReceived: { orderId } } : {}),
      };
    }
    if (!bodyClasses.includes('woocommerce-checkout')) return undefined;
    const errorState = this.extractCheckoutErrorMessage();
    if (errorState) {
      const totals = this.extractCheckoutTotals();
      return { step: 'error', ...(totals ? { totals } : {}), errorState };
    }
    if (this.isCheckoutProcessing()) {
      const totals = this.extractCheckoutTotals();
      return { step: 'processing', ...(totals ? { totals } : {}) };
    }
    const totals = this.extractCheckoutTotals();
    return { step: 'payment', ...(totals ? { totals } : {}) };
  }

  private setupCheckoutObserver(): void {
    if (this.checkoutObserver) return;
    const target = document.querySelector('form.woocommerce-checkout, .wp-block-woocommerce-checkout, #order_review');
    if (!target) return;
    this.checkoutObserver = new MutationObserver(() => {
      if (this.onUpdate) this.onUpdate();
    });
    this.checkoutObserver.observe(target, {
      childList: true, subtree: true, attributes: true,
      attributeFilter: ['class', 'disabled', 'aria-busy'],
    });
  }

  private teardownCheckoutObserver(): void {
    if (this.checkoutObserver) {
      this.checkoutObserver.disconnect();
      this.checkoutObserver = null;
    }
  }

  // WC Blocks cart pages use React store updates (/wp-json/wc/store/v1) instead of the classic
  // jQuery wc_fragments_refreshed event. Observe the cart items container for row additions/removals.
  private setupBlocksCartObserver(onUpdate: () => void): void {
    if (this.blocksCartObserver) return;
    // WC Blocks: observe Blocks cart container
    // Classic WC: wc_fragments_refreshed covers mutations, but observe the form as a fallback
    const container = document.querySelector<HTMLElement>(
      '.wc-block-cart-items, .wp-block-woocommerce-cart, .woocommerce-cart-form'
    );
    if (!container) return;
    // Use row count as change signal for Blocks; for classic, fall back to total child count
    const rowSel = '.wc-block-cart-items__row, tr.cart_item';
    let lastCount = container.querySelectorAll(rowSel).length;
    this.blocksCartObserver = new MutationObserver(() => {
      const current = container.querySelectorAll(rowSel).length;
      if (current !== lastCount) {
        lastCount = current;
        onUpdate();
      }
    });
    this.blocksCartObserver.observe(container, { childList: true, subtree: true });
  }

  private teardownBlocksCartObserver(): void {
    if (this.blocksCartObserver) {
      this.blocksCartObserver.disconnect();
      this.blocksCartObserver = null;
    }
  }

  private readVisibleLineItems(): { items: VisibleLineItem[]; truncated: boolean } {
    const rows = Array.from(document.querySelectorAll<HTMLElement>(
      'table.woocommerce-cart-form__contents tr.cart_item, ' +
      'table.cart tr.cart_item, ' +
      'tr.wc_cart_item, ' +
      '.wc-block-cart-items__row',
    ));
    const items: VisibleLineItem[] = [];
    for (const row of rows) {
      if (items.length >= CART_LINE_ITEM_CAP) return { items, truncated: true };
      const nameEl = row.querySelector<HTMLElement>(
        // WC Blocks: product name is <a class="wc-block-components-product-name">
        '.wc-block-components-product-name, ' +
        '.product-name a, .wc-block-cart-item__product-name a, ' +
        '.product-name, .wc-block-cart-item__product-name',
      );
      const productName = nameEl?.textContent?.trim().slice(0, 120);
      if (!productName) continue;
      const qtyEl = row.querySelector<HTMLInputElement>(
        'input.qty, .quantity input, .wc-block-cart-item__quantity input',
      );
      const quantity = qtyEl ? parseInt(qtyEl.value ?? '1', 10) : 1;
      const lineId: string | undefined = row.getAttribute('data-cart_item_key') ?? undefined;
      const linkEl = nameEl?.tagName === 'A'
        ? (nameEl as HTMLAnchorElement)
        : row.querySelector<HTMLAnchorElement>('.product-name a, .wc-block-cart-item__product-name a');
      let productLink: string | undefined;
      if (linkEl?.href) {
        try {
          const u = new URL(linkEl.href);
          productLink = u.pathname + (u.search || '');
        } catch { /* malformed href */ }
      }
      items.push({ productName, quantity, ...(lineId ? { lineId } : {}), ...(productLink ? { productLink } : {}) });
    }
    return { items, truncated: false };
  }

  private readCartTotal(): CartContext['total'] {
    const amount = this.readAmountFrom([
      '.cart-collaterals .order-total .woocommerce-Price-amount bdi',
      '.cart-collaterals .order-total .amount',
      '.wc-block-cart__totals-title + .wc-block-components-totals-footer-item .wc-block-formatted-money-amount',
      '.wc-block-components-totals-footer-item .wc-block-formatted-money-amount',
    ]);
    if (amount === null) return undefined;
    const wcParams   = (window as any).woocommerce_params;
    const wcSettings = (window as any).wcSettings?.currency;
    const currency: string = wcParams?.currency ?? wcSettings?.code ?? 'USD';
    return { amount, currency };
  }

  private readCartHasDiscount(): boolean {
    return !!(
      document.querySelector('.cart-discount, .coupon-discount, .wc-block-components-totals-coupon__list-item') ||
      document.querySelector('.woocommerce-remove-coupon')
    );
  }

  private detectCartContext(): CartContext {
    const emptyEl = document.querySelector('.cart-empty, .woocommerce-cart-form__empty-cart-message');
    const isEmpty = !!(
      emptyEl ||
      document.querySelector('.wc-empty-cart-message') ||
      (() => {
        const p = document.querySelector('.woocommerce-info');
        return p?.textContent?.toLowerCase().includes('cart is currently empty') ?? false;
      })()
    );
    if (isEmpty) return { isEmpty: true };
    const total = this.readCartTotal();
    const hasDiscount = this.readCartHasDiscount();
    const { items, truncated } = this.readVisibleLineItems();
    // readCartCount reads the mini-cart badge widget, which is absent on the cart page itself in
    // many themes (Astra included). Fall back to counting visible line item rows.
    const widgetCount = this.readCartCount();
    const itemCount = widgetCount ?? (items.length > 0 ? items.length + (truncated ? 1 : 0) : null);
    return {
      isEmpty: false,
      ...(itemCount !== null ? { itemCount } : {}),
      ...(total ? { total } : {}),
      ...(items.length > 0 ? { visibleLineItems: items } : {}),
      ...(truncated ? { visibleLineItemsTruncated: true } : {}),
      ...(hasDiscount ? { hasDiscount: true } : {}),
    };
  }
}
